import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "../api/axiosApi";
import { apiUrl } from "../constants/config";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import usePageTitle from "../hooks/usePageTitle";
import ImageNotAvailable from "../assets/Images/image_not_available.png";

const PaintingPage = () => {
    const { id } = useParams();
    const [painting, setPainting] = useState(null);
    const [mainIndex, setMainIndex] = useState(0);
    const [loading, setLoading] = useState(true);

    usePageTitle(painting ? `${painting.name} | Dinara Fariz` : "Dinara Fariz");

    useEffect(() => {
        const fetchPainting = async () => {
            try {
                const response = await axios.get(`/paintings/${id}`);
                setPainting(response.data);
                setMainIndex(0);
            } catch (error) {
                console.error("Ошибка при загрузке картины:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPainting();
    }, [id]);

    useEffect(() => {
        if (painting?.images?.length && mainIndex >= painting.images.length) {
            setMainIndex(0);
        }
    }, [painting, mainIndex]);

    const getImageSrc = (img) => {
        if (!img) return ImageNotAvailable;

        const imageValue =
            typeof img === "string"
                ? img
                : img?.url || "";

        if (!imageValue) return ImageNotAvailable;

        if (imageValue.startsWith("http")) {
            if (imageValue.includes("res.cloudinary.com")) {
                return imageValue.replace("/upload/", "/upload/f_auto,q_auto,w_1400/");
            }
            return imageValue;
        }

        return `${apiUrl}/${imageValue}`;
    };

    if (loading) {
        return (
            <div className="text-center py-10 text-gray-500">
                Loading...
            </div>
        );
    }

    if (!painting) {
        return (
            <div className="text-center py-10 text-gray-500">
                Painting not found
            </div>
        );
    }

    const images = painting.images?.length ? painting.images : [ImageNotAvailable];
    const mainImage = getImageSrc(images[mainIndex]);

    const handlePrev = () => {
        setMainIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setMainIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-4 md:py-12">
            <div className="flex flex-col md:flex-row items-start gap-6 md:gap-14">
                <div className="w-full md:w-1/2">
                    <div className="relative">
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={mainImage}
                                src={mainImage}
                                alt={painting.name}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.45 }}
                                className="w-full object-contain max-h-[72vh] md:max-h-[78vh]"
                            />
                        </AnimatePresence>

                        {images.length > 1 && (
                            <>
                                <button
                                    type="button"
                                    onClick={handlePrev}
                                    aria-label="Previous image"
                                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white transition p-2"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>

                                <button
                                    type="button"
                                    onClick={handleNext}
                                    aria-label="Next image"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white transition p-2"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </>
                        )}
                    </div>

                    {images.length > 1 && (
                        <div className="flex justify-center gap-3 mt-4 flex-wrap">
                            {images.map((img, index) => {
                                const thumbSrc = getImageSrc(img);
                                const thumbKey =
                                    typeof img === "string"
                                        ? img
                                        : img?.public_id || img?.url || index;

                                return (
                                    <button
                                        key={thumbKey}
                                        type="button"
                                        onClick={() => setMainIndex(index)}
                                        aria-label={`Open image ${index + 1}`}
                                        className="w-20 h-20"
                                    >
                                        <img
                                            src={thumbSrc}
                                            alt={`${painting.name} preview ${index + 1}`}
                                            className={`w-full h-full object-cover border transition ${
                                                index === mainIndex
                                                    ? "border-black"
                                                    : "border-gray-300 opacity-70 hover:opacity-100"
                                            }`}
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="w-full md:w-1/2 text-left space-y-4">
                    <h1 className="text-xl md:text-2xl font-light uppercase tracking-[0.1em] text-gray-800">
                        {painting.name}
                    </h1>

                    {!painting.published && (
                        <p className="text-sm uppercase tracking-[0.1em] text-gray-500">
                            Sold
                        </p>
                    )}

                    {painting.price && Number(painting.price) > 0 && (
                        <p>
                            ₸ {new Intl.NumberFormat("ru-RU").format(Number(painting.price))}
                        </p>
                    )}

                    {painting.description && (
                        <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                            {painting.description}
                        </p>
                    )}

                    <div className="pt-4">
                        <Link
                            to="/gallery"
                            className="inline-block text-sm uppercase tracking-[0.2em] text-gray-600 hover:text-black transition"
                        >
                            Back to Gallery
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaintingPage;